import React from 'react';
import { Box, Container, Grid, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import StraightenRoundedIcon from '@mui/icons-material/StraightenRounded';
import ConstructionRoundedIcon from '@mui/icons-material/ConstructionRounded';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import PageLayout from '../components/PageLayout';
import Section, { SectionHeading } from '../components/PageSection';
import {
  FeatureCard,
  FeatureIcon,
  CardTitle,
  CardText,
  DarkCard,
  CheckItem,
  accentAt,
  AccreditationNote,
} from '../components/ui';

const SITE = 'https://qasc.it';

// Anchor targets keep the original site's names (#auditing, #rischio,
// #modello, #alimentare) so the dropdown menu and old deep links keep working.
const Anchor = ({ id }) => (
  <Box id={id} sx={{ position: 'absolute', top: '-96px' }} aria-hidden="true" />
);

// Management systems followed by QAS Consulting (original "I NOSTRI PRINCIPALI
// SERVIZI" list).
const managementSystems = [
  'Sistema di gestione per la Qualità — UNI EN ISO 9001:2015',
  'Sistema di gestione per l’Ambiente — UNI EN ISO 14001:2015',
  'Sistema di gestione per la Sicurezza — UNI ISO 45001:2018',
  'Sistemi di gestione per l’Energia — ISO 50001',
  'Sistemi di gestione per la Responsabilità Sociale — SA 8000',
  'Sistemi di gestione per l’Automotive — IATF 16949:2016',
  'Sistemi di gestione per la saldatura — EN ISO 3834',
  'BRC',
  'IFS Logistics',
];

// Sicurezza sul lavoro services.
const safetyServices = [
  {
    icon: <DescriptionRoundedIcon />,
    title: 'Valutazione dei rischi',
    text: 'Documento di Valutazione dei Rischi (DVR) e relativo aggiornamento, con la valutazione del rischio stress lavoro-correlato e delle radiazioni ottiche artificiali (ROA); valutazione del rischio chimico secondo l’art. 223 del D.Lgs. 81/2008.',
  },
  {
    icon: <StraightenRoundedIcon />,
    title: 'Misure strumentali',
    text: 'Valutazione del rischio esposizione rumore/fonometria (art. 190 D.Lgs. 81/2008), valutazione del rischio vibrazioni (art. 202) e dei campi elettromagnetici negli ambienti di lavoro (Capo IV, Titolo VIII).',
  },
  {
    icon: <ConstructionRoundedIcon />,
    title: 'Cantieri',
    text: 'Stesura del POS (art. 17 comma 1) e del DUVRI (art. 26 comma 3); per la direttiva cantieri: assunzione del ruolo di CSP/CSE, stesura del PSC e implementazione dei documenti di cantiere.',
  },
  {
    icon: <LocalFireDepartmentRoundedIcon />,
    title: 'Antincendio',
    text: 'Valutazione del rischio incendio secondo il nuovo D.M. 3 settembre 2021, progettazione del piano di emergenza ed evacuazione, predisposizione del layout di evacuazione, sopralluoghi tecnici e conseguimento del certificato di prevenzione incendi (CPI).',
  },
  {
    icon: <SupportAgentRoundedIcon />,
    title: 'Servizio di RSPP esterno',
    text: 'Tra gli obblighi non delegabili del Datore di Lavoro c’è la nomina dell’RSPP. I nostri professionisti, con i titoli richiesti dall’Accordo Stato-Regioni per tutti i settori ATECO, tengono traccia delle scadenze, vi aggiornano sulle variazioni legislative e applicano le condotte richieste dalla normativa.',
  },
];

// Modello 231 — supporto offerto (original list).
const model231Support = [
  'Definizione del Codice Etico',
  'Identificazione dei rischi potenziali e mappa delle attività a rischio',
  'Creazione del modello organizzativo (parte generale + parte speciale)',
  'Predisposizione delle procedure interne per i diversi processi aziendali',
  'Sistema di deleghe e responsabilità — definizione del sistema sanzionatorio',
  'Formazione e informazione del personale sulle misure adottate',
  'L’Organismo di Vigilanza',
];

// Sicurezza alimentare — servizi (original list).
const foodServices = [
  'Valutazione dei rischi legati alla sicurezza alimentare',
  'Redazione e aggiornamento del manuale di autocontrollo HACCP',
  'Stesura del menù allergeni',
  'Verifica di standard di prodotto e di processo',
  'Stesura di schede tecniche di prodotto',
  'Sistema di rintracciabilità ai sensi del Reg. CE n. 178/02',
  'Etichette e tabelle nutrizionali (Reg. UE n. 1169/11 e s.m.i.)',
  'Disciplinari di produzione per marchi DOC, DOCG, IGP (Reg. UE 1151/12)',
];

function ConsulenzaPage() {
  return (
    <PageLayout
      seo={{
        title: 'Consulenza — Sistemi di Gestione, Sicurezza, Modello 231, HACCP | QAS Consulting',
        description:
          'Supporto concreto e diretto alle imprese: sistemi di gestione e auditing (ISO 9001, 14001, 45001, 50001, SA 8000, IATF 16949), sicurezza sul lavoro con DVR e servizio RSPP esterno, modello organizzativo 231 e sicurezza alimentare HACCP.',
        canonical: `${SITE}/consulenza`,
      }}
      hero={{
        kicker: 'Consulenza',
        title: 'Facciamo crescere le tue idee',
        subtitle:
          'QAS Consulting offre un supporto concreto e diretto alle imprese che intendono dotarsi di un sistema di gestione o essere in linea con la conformità legislativa in materia di sicurezza e responsabilità sociale.',
      }}
    >
      {/* (1) Sistemi di gestione e auditing */}
      <Section tone="light" sx={{ position: 'relative' }}>
        <Anchor id="auditing" />
        <Container maxWidth="lg">
          <SectionHeading
            kicker="Sistemi di gestione e auditing"
            title="Le buone prassi, messe nero su bianco"
            subtitle="Realizzare un sistema di gestione permette di tenere sotto controllo i processi interni e lavorare per obiettivi e piani di miglioramento: le buone prassi che già ogni giorno adottate vengono messe nero su bianco. I vantaggi: più efficienza dei processi, maggiore coinvolgimento del personale, diminuzione dei rischi aziendali e un’immagine che si distingue dalla concorrenza."
          />
          <Grid container spacing={2.5}>
            {managementSystems.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item}>
                <CheckItem>{item}</CheckItem>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Section>

      {/* (2) Sicurezza sul lavoro / valutazione del rischio */}
      <Section tone="mist" sx={{ position: 'relative' }}>
        <Anchor id="rischio" />
        <Container maxWidth="lg">
          <SectionHeading
            kicker="Sicurezza sul lavoro"
            title="Valutazione del rischio"
            subtitle="QAS Consulting fornisce servizi di consulenza per l’igiene, la salute e la sicurezza del lavoro dalla piccola alla grande impresa: redigiamo tutta la documentazione necessaria (D.Lgs. 81/08) con l’obiettivo di migliorare la sicurezza aziendale e superare in tutta tranquillità le ispezioni da parte degli organi esterni di controllo."
          />
          <Grid container spacing={4}>
            {safetyServices.map((m, i) => (
              <Grid item xs={12} sm={6} md={4} key={m.title}>
                <FeatureCard>
                  <FeatureIcon accent={accentAt(i)}>{m.icon}</FeatureIcon>
                  <CardTitle>{m.title}</CardTitle>
                  <CardText>{m.text}</CardText>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Section>

      {/* (3) Modello organizzativo 231 */}
      <Section tone="dark" sx={{ position: 'relative' }}>
        <Anchor id="modello" />
        <Container maxWidth="lg">
          <SectionHeading
            tone="dark"
            kicker="Modello organizzativo 231"
            title="Prevenire i reati, tutelare l’organizzazione"
            subtitle="Il Modello di organizzazione, gestione e controllo ai sensi del D.Lgs. 231/2001 è l’insieme delle regole e delle procedure organizzative volte a prevenire la commissione di reati. Adottarlo non è obbligatorio, ma costituisce causa di esclusione o limitazione della responsabilità dell’ente: studi professionali e grandi imprese possono così tutelare sé stessi e il proprio management."
          />
          <Grid container spacing={5} alignItems="flex-start">
            <Grid item xs={12} md={6}>
              <DarkCard>
                <Typography
                  sx={{ fontFamily: '"Sora", sans-serif', fontSize: '18px', fontWeight: 700, color: '#ffffff', mb: 2 }}
                >
                  Come lavoriamo
                </Typography>
                <Typography sx={{ fontSize: '14.5px', lineHeight: 1.8, color: 'rgba(255,255,255,0.78)', mb: 2 }}>
                  Vi accompagniamo nell’analisi e nello sviluppo del Modello di Organizzazione:
                  analizziamo l’azienda, realizziamo il Modello Organizzativo 231 e il Codice
                  Etico, organizziamo la formazione opportuna e definiamo gli audit periodici di
                  verifica.
                </Typography>
                <Typography sx={{ fontSize: '14.5px', lineHeight: 1.8, color: 'rgba(255,255,255,0.78)', mb: 2 }}>
                  L’intervento prevede un’analisi aziendale approfondita, estesa a tutti i livelli
                  organizzativi: una verifica documentale di carattere generale e interviste con i
                  soggetti che rivestono funzioni chiave, per l’analisi dei rischi e del controllo
                  interno.
                </Typography>
                <Typography sx={{ fontSize: '14.5px', lineHeight: 1.8, color: 'rgba(255,255,255,0.78)' }}>
                  Il check-up si conclude con un progetto di intervento che identifica le
                  criticità aziendali, per arrivare poi alla realizzazione e all’adozione del
                  modello.
                </Typography>
              </DarkCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <DarkCard>
                <Typography
                  sx={{ fontFamily: '"Sora", sans-serif', fontSize: '18px', fontWeight: 700, color: '#ffffff', mb: 2 }}
                >
                  Il nostro supporto
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 0, listStyle: 'none' }}>
                  {model231Support.map((s) => (
                    <Box
                      component="li"
                      key={s}
                      sx={{
                        py: '9px',
                        fontSize: '14.5px',
                        lineHeight: 1.6,
                        color: 'rgba(255,255,255,0.78)',
                        borderBottom: '1px solid rgba(255,255,255,0.10)',
                        '&:last-of-type': { borderBottom: 'none' },
                        '&:before': { content: '"— "', color: '#8fc1d6' },
                      }}
                    >
                      {s}
                    </Box>
                  ))}
                </Box>
              </DarkCard>
            </Grid>
          </Grid>
        </Container>
      </Section>

      {/* (4) Sicurezza alimentare */}
      <Section tone="light" sx={{ position: 'relative' }}>
        <Anchor id="alimentare" />
        <Container maxWidth="lg">
          <SectionHeading
            kicker="Sicurezza alimentare"
            title="HACCP: prevenzione e controllo obbligatori"
            subtitle="Lo standard HACCP è un piano di autocontrollo che si sviluppa in base a 7 principi ed è obbligatorio per tutti coloro che operano nel settore alimentare — produzione primaria, vendita al dettaglio, stoccaggio o somministrazione. Serve a garantire che gli alimenti arrivino al consumatore finale prodotti in maniera sicura e non contaminati; lo standard è uguale per tutte le aziende, ma il manuale HACCP va personalizzato per ognuna."
          />
          <Grid container spacing={2.5}>
            {foodServices.map((item) => (
              <Grid item xs={12} sm={6} md={3} key={item}>
                <CheckItem>{item}</CheckItem>
              </Grid>
            ))}
          </Grid>
          <AccreditationNote />
          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <Typography
              component={RouterLink}
              to="/contatti"
              sx={{
                fontFamily: '"Sora", sans-serif',
                fontWeight: 600,
                fontSize: '15.5px',
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Contattaci →
            </Typography>
          </Box>
        </Container>
      </Section>
    </PageLayout>
  );
}

export default ConsulenzaPage;
